import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorToolbar, RICH_TEXT_HEADING_LEVELS } from './editor-toolbar';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  editable?: boolean;
}

export function RichTextEditor({
  content,
  onChange,
  placeholder = 'Write something...',
  className,
  editable = true,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      // Keep the full default heading schema so pre-existing notes containing
      // h4–h6 aren't downgraded to paragraphs on edit; the toolbar still exposes
      // only RICH_TEXT_HEADING_LEVELS to match the vacancy description editor.
      // StarterKit v3 bundles Link; disable it so the configured Link below is
      // the only `link` extension (avoids TipTap's duplicate-extension warning).
      StarterKit.configure({ link: false }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[200px] p-4',
          className
        ),
      },
    },
  });

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden">
      {editable && <EditorToolbar editor={editor} headingLevels={RICH_TEXT_HEADING_LEVELS} />}
      <div className="max-h-[400px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
